import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductCompareReturnUrl,
    getProductIdsForCompare,
    getProductsForCompare,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import loadProductIdsToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/LoadProductIdsToCompare";
import loadProductCompareReturnUrl from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/LoadProductReturnUrl";
import loadProductsToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/LoadProductsToCompare";
import removeAllProductsFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveAllProductsFromCompare";
import removeProductIdToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductCarouselProductFull, {
    ProductCarouselProductFullStyles,
} from "@insite/content-library/Components/ProductCarouselProductFull";
/* Custom Code
import ProductCompareCarouselComponent, {
    CarouselStyles,
    ProductCarouselSlideDetails,
} from "@insite/content-library/Components/ProductCompareCarousel"; */
import Button, { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Custom Code */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getProductIds } from "@insite/client-framework/Services/ProductCompareService";
import siteMessage from "@insite/client-framework/SiteMessage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import ga from "@store/Framework/Common/GoogleAnalytics";
import getMatchingDataFromDictionary from "@store/Framework/Common/Utilities/getMatchingDataFromDictionary";
import TrashFill from "@store/Library/Components/Icons/Trash/TrashFill";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import OrderHeader from "@store/Library/Components/Toolkit/Order/OrderHeader";
import ProductCarouselProductCondensed from "@store/Overrides/Components/ProductCarouselProductCondensed";
import ProductCompareCarouselComponent, {
    CarouselStyles,
    ProductCarouselSlideDetails,
} from "@store/Overrides/Components/ProductCompareCarousel";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { productCompareProps } from "@store/Themes/common/props/productCompare";
/* Custom Code */

const enum fields {
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    productIds: getProductIdsForCompare(state),
    products: getProductsForCompare(state),
    homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
    returnUrl: getProductCompareReturnUrl(state),
    // Custom Code
    productInfoByProductId:
        state.components.productInfoLists.productInfoListById[constants.productCompare.carouselId]
            ?.productInfoByProductId,
    hasProducts: Boolean(getProductIdsForCompare(state).length || getProductIds().length),
});

const mapDispatchToProps = {
    loadProductIdsToCompare,
    loadProductCompareReturnUrl,
    loadProductsToCompare,
    removeProductIdToCompare,
    removeAllProductsFromCompare,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface ProductCompareCarouselStyles {
    container?: GridContainerProps;
    navigationLinkGridItem?: GridItemProps;
    navigationLink?: LinkPresentationProps;
    headerGridItem?: GridItemProps;
    headerContainer?: GridContainerProps;
    headerTextGridItem?: GridItemProps;
    headerText?: TypographyPresentationProps;
    headerButtonsGridItem?: GridItemProps;
    headerRemoveAllButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    productFull?: ProductCarouselProductFullStyles;
    removeProductFromCompareClickable?: ClickablePresentationProps;
    removeProductFromCompareButtonIcon?: ButtonIconPresentationProps;
    carousel?: CarouselStyles;
    // Custom Code
    headerRemoveAllLink?: LinkPresentationProps;
    condensedActions?: InjectableCss;
    removeProductFromCompareButton?: ButtonPresentationProps;
    messageInfosBox?: InfosBoxProps;
}

export const ProductCompareCarouselStyles: ProductCompareCarouselStyles = {
    headerRemoveAllLink: linkGetActionLinkProps(TrashFill),
    removeProductFromCompareButton: productCompareProps.removeProductFromCompareButton,
    removeProductFromCompareButtonIcon: productCompareProps.removeProductFromCompareButtonIcon,
    condensedActions: {
        css: css`
            flex-basis: 5%;
        `,
    },
    messageInfosBox: { type: "warning" },
};

const mapProductsToSlideAttributeTypes = (products: ProductModel[]) =>
    products
        .map(a => a.attributeTypes || [])
        .reduce((acc, current) => {
            return [...(acc || []), ...current];
        }, [])
        .filter(
            (current, index, self) => current.isComparable && index === self.findIndex(a => a.name === current.name),
        );

const styles = ProductCompareCarouselStyles;

const ProductCompareCarousel = ({
    history,
    productIds,
    products,
    homePageUrl,
    returnUrl,
    fields,
    loadProductIdsToCompare,
    loadProductCompareReturnUrl,
    loadProductsToCompare,
    removeProductIdToCompare,
    removeAllProductsFromCompare,
    // Custom Code
    productInfoByProductId,
    hasProducts,
}: Props) => {
    const productCompareCarouselId = constants.productCompare.carouselId; // Custom Code
    const [lastProductCount, setLastProductCount] = useState<number>(0);

    const [slideDetails, setSlideDetails] = useState<ProductCarouselSlideDetails>({
        products: [],
        attributeTypes: [],
        slides: [],
    });

    /* Custom Code */
    const [canTrackViewItemList, setCanTrackViewItemList] = useState(false);
    const productInfos = getMatchingDataFromDictionary(slideDetails.products, productInfoByProductId);
    const trackViewItemList = ga.use.trackViewItemList(
        {
            item_list_id: "product_comparison",
            item_list_name: "Product Comparison",
            items: slideDetails.products,
        },
        { productInfos },
    );

    ga.use.productInfosCallback(
        productInfos,
        () => {
            setCanTrackViewItemList(false);
            trackViewItemList();
        },
        canTrackViewItemList,
    );
    /* Custom Code */

    useEffect(() => {
        // We load in the ProductIds to Compare from storage
        loadProductIdsToCompare();
        loadProductCompareReturnUrl();
    }, []);

    useEffect(() => {
        // Load in products from the ProductCompareService Compare Storage
        if (productIds.length === 0) {
            if (lastProductCount > 0) {
                // Navigate back to last page or homepage
                let url = homePageUrl ?? "";
                if (returnUrl) {
                    url = returnUrl;
                }
                history.push(url);
            }
            return;
        }

        loadProductsToCompare({ id: productCompareCarouselId, productIds, includeAttributeTypes: true });
        setCanTrackViewItemList(true); // Custom Code
    }, [productIds]);

    const mapProductsToSlides = (
        products: ProductModel[],
        productCompareCarouselId: string,
        styles: ProductCompareCarouselStyles,
        removeProductIdToCompare: (productId: string) => void,
    ) =>
        products.map(product => ({
            id: product.id,
            renderComponent: function ProductCarouselProductCondensedRenderer() {
                return (
                    /* Custom Code */
                    <ProductCarouselProductCondensed
                        carouselId={productCompareCarouselId}
                        product={product}
                        showImage={true}
                        showBrand={false}
                        showTitle={true}
                        showPartNumbers={false}
                        showPrice={false}
                        showAddToCart={true}
                        showAddToList={false}
                        showUnitOfMeasure={false}
                        condensedActionsTemplate={(product: ProductModel) => {
                            return (
                                <StyledWrapper {...styles.condensedActions}>
                                    <Button
                                        {...styles.removeProductFromCompareButton}
                                        onClick={() => removeProductIdToCompare(product.id)}
                                        data-test-selector="productCompareRemoveProductButton"
                                        data-test-key={product.id}
                                    >
                                        <VisuallyHidden>
                                            {translate("Remove Item {0} from Compare", product.productTitle)}
                                        </VisuallyHidden>
                                        <ButtonIcon {...styles.removeProductFromCompareButtonIcon} />
                                    </Button>
                                </StyledWrapper>
                            );
                        }}
                    />
                    /* Custom Code */
                );
            },
        }));

    useEffect(() => {
        if (products.length === 0) {
            return;
        }
        setSlideDetails({
            products,
            attributeTypes: mapProductsToSlideAttributeTypes(products),
            slides: mapProductsToSlides(products, productCompareCarouselId, styles, (productId: string) =>
                removeProductIdToCompare({ productId }),
            ),
        });
        setLastProductCount(products.length);
    }, [products]);

    const goBack = () => {
        if (returnUrl) {
            history.push(returnUrl);
        }
    };

    const onRemoveAll = () => {
        /* Moved - removeAllProductsFromCompare - down */
        let url = homePageUrl ?? "";
        if (returnUrl) {
            url = returnUrl;
        }
        history.push(url);
        removeAllProductsFromCompare(); // Custom Code
    };

    const linkBack: PageLinkModel = { url: returnUrl || "", title: "", id: "" }; // Custom Code

    return (
        /* Custom Code */
        <>
            <OrderHeader title={translate("Compare Products")} linkBack={products.length >= 2 ? linkBack : undefined}>
                {products.length >= 2 ? (
                    <Link
                        {...styles.headerRemoveAllLink}
                        onClick={onRemoveAll}
                        data-test-selector="productCompareRemoveAllButton"
                    >
                        {translate("Remove All")}
                        <VisuallyHidden>
                            {translate("{0} Products from Compare", slideDetails.products.length.toString())}
                        </VisuallyHidden>
                    </Link>
                ) : null}
            </OrderHeader>
            {!hasProducts ? (
                <InfosBox {...styles.messageInfosBox}>{siteMessage("ProductCompare_NoProductToCompare")}</InfosBox>
            ) : (
                <ProductCompareCarouselComponent
                    id={productCompareCarouselId}
                    extendedStyles={styles.carousel}
                    maxNumberOfColumns={constants.productCompare.maxNumberOfColumns}
                    title={translate("Compare {0} Products", slideDetails.products.length.toString())}
                    hideTitle={true}
                    slideDetails={slideDetails}
                    returnUrl={returnUrl}
                />
            )}
        </>
        /* Custom Code */
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductCompareCarousel)),
    definition: {
        group: "Product Compare",
        icon: "List",
        allowedContexts: ["ProductComparePage"],
        fieldDefinitions: [
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
